/**
 * Base URL for API JSON when the app and API are not on the same origin.
 * Build-time: REACT_APP_API_URL=https://api.example.com
 * Runtime (no rebuild): put client/public/config.json → build/config.json with { "apiUrl": "..." }
 *
 * If unset: same-origin relative paths (/api/...). Works when:
 * - CRA dev (setupProxy.js forwards /api), or
 * - Express serves client/build and API together (npm run build in client, npm start in server).
 */
let runtimeApiBase = null;
let runtimeConfigPromise = null;

function normalizeApiBaseUrl(raw) {
  if (!raw) return "";
  let value = String(raw).trim();
  value = value.replace(/\/+$/, "");
  value = value.replace(/\/api$/i, "");
  return value;
}

export function ensureApiConfig() {
  if (runtimeApiBase !== null) return Promise.resolve();
  if (process.env.REACT_APP_API_URL) {
    runtimeApiBase = "";
    return Promise.resolve();
  }
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = (async () => {
      try {
        const res = await fetch("/config.json", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          const url = data?.apiUrl;
          runtimeApiBase = normalizeApiBaseUrl(url || "");
        } else {
          runtimeApiBase = "";
        }
      } catch {
        runtimeApiBase = "";
      }
    })();
  }
  return runtimeConfigPromise;
}

export function getApiBaseUrl() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw) return normalizeApiBaseUrl(raw);
  if (runtimeApiBase) return runtimeApiBase;
  return "";
}

/**
 * Resolves asset URLs for <img>/<video>. App-hosted files under /gallery/ stay same-origin (CRA public/).
 */
export function mediaAbsoluteUrl(relativePath) {
  if (!relativePath) return "";
  if (relativePath.startsWith("http")) return relativePath;
  if (relativePath.startsWith("/gallery/")) return relativePath;
  const base = getApiBaseUrl();
  if (base) return `${base}${relativePath}`;
  return relativePath;
}

export function apiUrl(path) {
  const base = getApiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}
